import { Alert, Button, Form } from "react-bootstrap";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { defineMessages, FormattedMessage } from "react-intl";
import styled from "@emotion/styled";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAt, faBuilding, faUser } from "@fortawesome/free-solid-svg-icons";
import Link from "next/link";
import { useState } from "react";
import { useYupSchema } from "../../hooks/useYupSchema";
import { useYupResolver } from "../../hooks/useYupResolver";
import Input from "../form/Input";
import Checkbox from "../form/Checkbox";

type FormValues = {
  name: string;
  company: string;
  email: string;
  acceptPrivacyPolicy: true;
};

const messages = defineMessages({
  nameLabel: {
    id: "PrivateBetaForm.nameLabel",
    defaultMessage: "Name",
  },
  namePlaceholder: {
    id: "PrivateBetaForm.namePlaceholder",
    defaultMessage: "Ford Prefect",
  },
  companyLabel: {
    id: "PrivateBetaForm.companyLabel",
    defaultMessage: "Company",
  },
  companyPlaceholder: {
    id: "PrivateBetaForm.companyPlaceholder",
    defaultMessage: "Intergalactic Research & Travel Consultancy",
  },
  emailLabel: {
    id: "PrivateBetaForm.emailLabel",
    defaultMessage: "Email",
  },
  emailPlaceholder: {
    id: "PrivateBetaForm.emailPlaceholder",
    defaultMessage: "ford.prefect@example.org",
  },
});

const StyledForm = styled(Form)`
  position: relative;
  width: 100%;
`;

enum FormState {
  filling,
  submitting,
  success,
  error,
}

const PrivateBetaForm = () => {
  const [status, setStatus] = useState(FormState.filling);

  const schema = useYupSchema(() => ({
    name: yup.string().required(),
    company: yup.string().required(),
    email: yup.string().email().required(),
    acceptPrivacyPolicy: yup.boolean().isTrue().required(),
  }));

  const resolver = useYupResolver(schema);

  const {
    register,
    handleSubmit,
    formState: { isValid, errors, submitCount },
  } = useForm<FormValues>({
    resolver,
    defaultValues: {},
  });

  const onSubmit = handleSubmit(async ({ name, email, company, acceptPrivacyPolicy }, event) => {
    if (status === FormState.submitting) {
      return;
    }

    event?.preventDefault();
    setStatus(FormState.submitting);

    try {
      await fetch(
        "https://prod-02.switzerlandnorth.logic.azure.com:443/workflows/20f28ba83216454d9c8a0dc11dfb08ac/triggers/manual/paths/invoke?api-version=2016-06-01&sp=%2Ftriggers%2Fmanual%2Frun&sv=1.0&sig=eZykrZKSW9QdumpiTPdIMwNYmt5wcQRvapwIcZ8kBGI",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            name,
            company,
            email,
            acceptPrivacyPolicy,
          }),
        },
      );

      setStatus(FormState.success);
    } catch {
      setStatus(FormState.error);
    }
  });

  return (
    <StyledForm validated={isValid} onSubmit={onSubmit}>
      <Input
        name="name"
        label={messages.nameLabel}
        placeholder={messages.namePlaceholder}
        register={register}
        errors={errors}
        data-testid="name"
        prefix={<FontAwesomeIcon icon={faUser} />}
      />

      <Input
        name="company"
        label={messages.companyLabel}
        placeholder={messages.companyPlaceholder}
        register={register}
        errors={errors}
        data-testid="company"
        prefix={<FontAwesomeIcon icon={faBuilding} />}
      />

      <Input
        name="email"
        label={messages.emailLabel}
        placeholder={messages.emailPlaceholder}
        register={register}
        errors={errors}
        data-testid="email"
        prefix={<FontAwesomeIcon icon={faAt} />}
      />

      <Checkbox
        name="acceptPrivacyPolicy"
        label={
          <FormattedMessage
            id="PrivateBetaForm.acceptPrivacyPolicy"
            defaultMessage="I have read the {privacyPolicy} and accept its terms."
            values={{
              privacyPolicy: (
                <Link href="/privacy-policy" target="_blank" className="text-white">
                  <FormattedMessage
                    id="PrivateBetaForm.privacyPolicy"
                    defaultMessage="privacy policy"
                  />
                </Link>
              ),
            }}
          />
        }
        register={register}
        errors={errors}
        data-testid="accept-privacy-policy"
      />

      <Button
        variant="secondary"
        type="submit"
        className="w-100 mt-2"
        disabled={(submitCount > 0 && !isValid) || status === FormState.submitting}
      >
        <FormattedMessage
          id="PrivateBetaForm.submit"
          defaultMessage="Request Private Beta Access"
        />
      </Button>

      {status === FormState.error && (
        <Alert variant="danger" className="mt-2">
          <FormattedMessage
            id="PrivateBetaForm.error"
            defaultMessage="An error occurred. Please report this problem."
          />
        </Alert>
      )}

      {status === FormState.success && (
        <Alert variant="success" className="mt-2">
          <FormattedMessage
            id="PrivateBetaForm.success"
            defaultMessage="Thank you for signing up! We'll be in touch soon."
          />
        </Alert>
      )}
    </StyledForm>
  );
};

export default PrivateBetaForm;
